/**
 * What: Shared parsing for Shopify app API error responses.
 * Transport / data-flow: Reads JSON error payloads returned by the Worker `/api/*` routes.
 *
 * Why this exists: the Worker's `error` field is developer prose (D1 errors, file paths, API
 * keys included) and must never be rendered as-is. This module is the one place that reads
 * that field, so it is also the one place that must never hand it back as displayable text.
 *
 * FOUND-F3 (#4487): the Worker now fills a merchant-safe `merchantMessage` on EVERY `/api` error
 * envelope — both the `ShopifyOperationError` routes and the plain `c.json` routes (via
 * `shopifyApiErrorBody`), resolved from `@testfeed/product-language` by error code. This module parses
 * that field and renders it; the app no longer maps a code to a sentence of its own, and no caller
 * passes its own copy as a last resort. The backend prose survives only on `diagnostic`, for logs. A
 * response that carried no `merchantMessage` (a body that was not JSON, or a route that somehow shipped
 * no words) fails loud: `throwMerchantFacingApiError` throws `ShopifyApiRequestError` with only the
 * code, and `readShopifyApiError` returns a null `merchantMessage` its caller must fail loud on. The
 * app never invents wording.
 */
import { MerchantFacingError, describeErrorForDiagnostics } from './merchantErrors'

type ShopifyApiErrorPayload = {
  error?: string
  code?: string
  merchantMessage?: string
  reconnectUrl?: string
  correlationId?: string
}

export type ShopifyApiErrorResult = {
  /** Merchant-safe wording the Worker sent for this error, or null when it sent none. Never backend prose; never invented. */
  merchantMessage: string | null
  /** Machine-readable Worker `code`, when present. Never render this. */
  code: string | null
  /** The backend's raw error text (or parse failure detail), for `console.error`/logging only. Never render this. */
  diagnostic: string | null
  reconnectUrl: string | null
  /** Worker correlation ID for joining this browser failure to its request trace. */
  correlationId: string | null
}

/** The parts of a failed Worker response these helpers read; satisfied by `Response` and the typed client's `ClientResponse`. */
type ShopifyApiErrorResponse = Pick<Response, 'status' | 'json'> & {
  headers?: Pick<Headers, 'get'>
}

/** An `/api` request that failed with no merchant wording to show. Carries the code/status so the lifecycle
 *  can name it (`pageErrorFrom`) and the page can show the server words it already holds for this state. */
export class ShopifyApiRequestError extends Error {
  readonly status: number
  readonly code?: string
  readonly reconnectUrl?: string
  readonly correlationId?: string
  constructor(status: number, code: string | null, reconnectUrl?: string | null, correlationId?: string | null) {
    super(`SHOPIFY_API_REQUEST_FAILED:${code ?? status}`)
    this.name = 'ShopifyApiRequestError'
    this.status = status
    if (code) this.code = code
    if (reconnectUrl) this.reconnectUrl = reconnectUrl
    if (correlationId) this.correlationId = correlationId
  }
}

function readCorrelationId(response: ShopifyApiErrorResponse): string | null {
  const header = response.headers?.get('X-Correlation-Id')?.trim()
  return header ? header.slice(0, 256) : null
}

/** Reads the server-sent fields of a failed Worker response. `merchantMessage` is the Worker's merchant copy or null. */
async function readShopifyApiErrorPayload(response: ShopifyApiErrorResponse): Promise<ShopifyApiErrorResult> {
  const responseCorrelationId = readCorrelationId(response)
  try {
    const payload = (await response.json()) as ShopifyApiErrorPayload
    return {
      merchantMessage:
        typeof payload?.merchantMessage === 'string' && payload.merchantMessage.length > 0
          ? payload.merchantMessage
          : null,
      code: typeof payload?.code === 'string' ? payload.code : null,
      diagnostic: typeof payload?.error === 'string' ? payload.error : null,
      reconnectUrl: typeof payload?.reconnectUrl === 'string' ? payload.reconnectUrl : null,
      correlationId:
        responseCorrelationId ??
        (typeof payload?.correlationId === 'string' && payload.correlationId.trim()
          ? payload.correlationId.trim().slice(0, 256)
          : null),
    }
  } catch (e) {
    console.error(e)
    return {
      merchantMessage: null,
      code: null,
      diagnostic: describeErrorForDiagnostics(e),
      reconnectUrl: null,
      correlationId: responseCorrelationId,
    }
  }
}

/**
 * Reads a Worker `/api/*` error response into the Worker's merchant-safe wording plus a diagnostic for
 * logs and an optional `reconnectUrl`. `merchantMessage` is whatever the Worker sent, or null; the app
 * never substitutes copy of its own. A caller that renders the message must fail loud (throw
 * `ShopifyApiRequestError`) when it is null. The backend's `error` prose is captured only in `diagnostic`.
 */
export async function readShopifyApiError(response: ShopifyApiErrorResponse): Promise<ShopifyApiErrorResult> {
  return readShopifyApiErrorPayload(response)
}

/**
 * Reads a failed Worker response and throws its merchant-safe wording as a `MerchantFacingError`,
 * logging the backend diagnostic (if any) under `logContext` first. Always throws; never returns.
 *
 * Every enriched `/api` route sends a `merchantMessage`, which is thrown. A response with no server
 * wording throws a `ShopifyApiRequestError` carrying only the code — fail loud, no invented sentence —
 * so the page renders the server words it already holds.
 */
export async function throwMerchantFacingApiError(
  response: ShopifyApiErrorResponse,
  logContext: string,
): Promise<never> {
  const parsed = await readShopifyApiErrorPayload(response)
  if (parsed.diagnostic) console.error(`[${logContext}]`, parsed.diagnostic)
  if (parsed.merchantMessage)
    throw new MerchantFacingError(parsed.merchantMessage, {
      status: response.status,
      code: parsed.code,
      reconnectUrl: parsed.reconnectUrl,
      correlationId: parsed.correlationId,
    })
  throw new ShopifyApiRequestError(response.status, parsed.code, parsed.reconnectUrl, parsed.correlationId)
}
